import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { enabledProviders, PROVIDER_NAMES, signInWithProvider, type OAuthProvider } from "../lib/auth";
import { env } from "../lib/env";
import { DISCLAIMER, ErrorMessage, Wordmark } from "./ui";

/** Auth frame (web theme): Cupertino Titanium canvas with a frosted white card centred on it. */
export function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-dvh p-3 sm:p-4 lg:p-5">
      <div className="glass-priority-card relative flex w-full flex-col items-center rounded-card px-3 pb-8 pt-20 sm:px-4 sm:pb-10 sm:pt-24 md:rounded-card-lg md:pt-28">
        <a href={env.siteUrl} aria-label="StudyWard home" className="absolute left-5 top-5 z-10 md:left-8 md:top-8">
          <Wordmark />
        </a>
        <main className="relative z-10 my-auto w-full max-w-md rounded-card bg-surface-card p-6 shadow-raised sm:p-8 md:p-10">
          <div className="text-center">
            <img src="/owl-head.webp" alt="" width={64} height={64} className="mx-auto hidden size-16 sm:block" />
            <h1 className="mt-3 text-h1 font-medium tracking-tight">{title}</h1>
            {subtitle && <p className="mt-1 text-small text-text-secondary">{subtitle}</p>}
          </div>
          <div className="mt-6 flex flex-col gap-4 text-left">{children}</div>
        </main>
        <p className="mt-6 text-caption text-text-primary">{DISCLAIMER}</p>
      </div>
    </div>
  );
}

/** Google / Apple buttons — only the providers switched on in Supabase Auth are shown. */
export function SocialButtons({ rememberMe }: { rememberMe?: boolean } = {}) {
  const providers = useQuery({ queryKey: ["auth-providers"], queryFn: enabledProviders, staleTime: 10 * 60_000 });
  const [pending, setPending] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = providers.data ?? [];
  if (list.length === 0) return null;

  const start = (p: OAuthProvider) => {
    setPending(p);
    setError(null);
    signInWithProvider(p, rememberMe).catch((e: unknown) => {
      setPending(null);
      setError(e instanceof Error ? e.message : "Sign-in failed. Try again.");
    });
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        {list.map((p) => (
          <button
            key={p}
            type="button"
            disabled={pending !== null}
            aria-busy={pending === p || undefined}
            onClick={() => start(p)}
            className="flex min-h-12 w-full items-center justify-center gap-3 rounded-pill bg-surface-card font-medium text-text-primary shadow-card transition-colors hover:bg-primary-soft disabled:cursor-not-allowed disabled:text-text-muted"
          >
            <img src={`/brand/${p}.svg`} alt="" width={20} height={20} className="size-5" />
            Continue with {PROVIDER_NAMES[p]}
          </button>
        ))}
      </div>
      {error && <ErrorMessage message={error} />}
      <div className="flex items-center gap-3 text-caption uppercase tracking-widest text-text-muted before:h-px before:flex-1 before:bg-border-strong after:h-px after:flex-1 after:bg-border-strong">
        or with email
      </div>
    </>
  );
}

